const SIZE = 10
const idOf = record => String(record?.id || record?.raw?.id || record?.hs_object_id || '')
const signature = value => JSON.stringify(value)

// Explicit batches use a separate endpoint. An older backend must return 404,
// never interpret an ignored recordIds option as permission to scan/write all.
export async function runAssignmentMap({ records, call, reason, authorizedBy = null,
  scope = 'all', tradeBodies = null, unassignedOnly = false, dryRun = true,
  preview = null, onProgress, onVerified } = {}) {
  const ids = [...new Set((records || []).map(idOf))]
  const spec = { ids, reason: String(reason || '').trim(), authorizedBy, scope, tradeBodies, unassignedOnly }
  const result = { ok: false, dryRun, total: ids.length, reviewed: 0, planned: 0,
    saved: 0, verified: 0, changed: 0, skipped: 0, conflicts: 0, locked: 0,
    unchanged: 0, failed: 0, decisions: [], changes: [], runIds: [], uncertainIds: [] }
  if (!ids.length || ids.some(id => !/^\d{1,30}$/.test(id)) || !spec.reason) {
    return { ...result, note: 'Nothing ran. A reason and explicit championship IDs are required.' }
  }
  const binding = signature(spec)
  if (!dryRun && (!preview?.ok || !preview.dryRun || preview.binding !== binding)) {
    return { ...result, note: 'Nothing ran. Preview this exact selection and reason again before applying it.' }
  }
  const proposed = dryRun ? ids : preview.decisions.filter(row => row.outcome === 'proposed').map(row => row.id)
  if (!dryRun) {
    // Carry every original exception and preserved owner into the final audit.
    // Applying the prepared subset must not erase the unprepared remainder.
    result.decisions.push(...preview.decisions.filter(row => row.outcome !== 'proposed'))
    result.skipped = preview.skipped; result.locked = preview.locked
    result.conflicts = preview.conflicts; result.unchanged = preview.unchanged
  }
  const bindings = new Map((preview?.decisions || []).map(row => [row.id, row.binding]))
  for (let offset = 0; offset < proposed.length; offset += SIZE) {
    const recordIds = proposed.slice(offset, offset + SIZE)
    try {
      const response = await call({ scope, reason: spec.reason, authorizedBy, tradeBodies,
        unassignedOnly, recordIds, dryRun,
        ...(!dryRun ? { previewBindings: Object.fromEntries(recordIds.map(id => [id, bindings.get(id)])) } : {}) })
      if (response?.ok !== true || response.dryRun !== dryRun
        || response.selection?.kind !== 'explicit-record-ids'
        || signature(response.selection.recordIds) !== signature(recordIds)
        || !Array.isArray(response.decisions) || !Array.isArray(response.changes)
        || response.decisions.length !== recordIds.length
        || new Set(response.decisions.map(row => row.id)).size !== recordIds.length
        || response.decisions.some(row => !recordIds.includes(row.id))
        || response.changes.some(row => !recordIds.includes(row.id) || row.verified !== true)
        || new Set(response.changes.map(row => row.id)).size !== response.changes.length
        || Number(response.tally?.verified || 0) !== response.changes.length
        || (dryRun && Number(response.tally?.wrote || 0) !== response.decisions.filter(row => row.outcome === 'proposed').length)) {
        throw new Error('The server did not return a receipt for this exact selection.')
      }
      result.runIds.push(response.runId)
      result.reviewed += recordIds.length
      const tally = response.tally || {}
      result.planned += dryRun ? Number(tally.wrote || 0) : 0
      result.saved += dryRun ? 0 : Number(tally.wrote || 0)
      result.verified += Number(tally.verified || 0)
      result.skipped += Number(tally.skipped || 0)
      result.conflicts += Number(tally.conflicts || 0)
      result.locked += Number(tally.lockedSkipped || 0)
      result.unchanged += Number(tally.unchanged || 0)
      result.failed += Number(tally.failed || 0)
      result.decisions.push(...response.decisions)
      result.changes.push(...response.changes)
      result.changed = result.verified
      if (!dryRun && response.changes.length) onVerified?.(response.changes)
      onProgress?.({ done: result.reviewed, total: proposed.length, verified: result.verified })
      if (result.failed || (!dryRun && result.saved !== result.verified)) {
        throw new Error('A batch failed or could not be verified. Inspect its receipt before continuing.')
      }
    } catch (error) {
      if (!dryRun) result.uncertainIds = recordIds.filter(id => !result.changes.some(row => row.id === id))
      const reported = new Set(result.decisions.map(row => row.id))
      for (const id of proposed) if (!reported.has(id)) result.decisions.push({ id,
        outcome: result.uncertainIds.includes(id) ? 'unverified-result' : 'not-attempted',
        reason: result.uncertainIds.includes(id) ? 'Inspect the current record before retrying.' : 'Stopped after an earlier failed batch.' })
      return { ...result, note: `Stopped after ${result.reviewed} of ${proposed.length} selected records. `
        + `${result.verified} assignments verified. ${error?.message || error} No automatic retry was made.` }
    }
  }
  result.ok = true
  result.binding = binding
  result.note = dryRun
    ? `Preview: ${result.planned} changes from ${ids.length} selected records; ${result.skipped} need a rule or are excluded, `
      + `${result.locked} locked and ${result.conflicts} conflicts. Nothing written.`
    : `${result.verified} assignments saved and verified; ${result.unchanged} existing owners preserved. `
      + 'Unmapped, locked and excluded records remain in this audit.'
  return result
}
